import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { BoxSeriesType, QualityType } from '@/api/enum/common.enum';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { BOX_SERIES_TYPE, PUBLISHED_LISTB } from '@/common/constants/common';
import { BlindBoxGameDTO, BoxFilter } from '@/api/DTO/exchange/box.interface';
import {
	useGetBoxTreasureListQuery,
	useGetBoxTreasureSelectedListQuery,
	useUpdateBoxTreasureListMutation,
} from '@/api/exchange/boxApiSlice';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import { useParams } from 'react-router-dom';
import PublishedChip from '@/common/components/PublishedChip';

interface Props {}

export default function AddTreasureDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [selectedData, setSelectedData] = useState<any[]>([]);
	const { boxId } = useParams();
	const [updateBoxTreasureList] = useUpdateBoxTreasureListMutation();

	const qualityList = [
		{ id: QualityType.Normal, name: t('chartletList.normal') },
		{ id: QualityType.Rare, name: t('chartletList.rare') },
		{ id: QualityType.Precious, name: t('chartletList.precious') },
		{ id: QualityType.SuperPrecious, name: t('chartletList.superPrecious') },
	];
	const defaultPropertyFilter: BoxFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		gameTypeList: [],
		id: boxId,
		selectedList: [0, 1],
	};
	const defaultValues: DefaultValues<BoxFilter> = defaultPropertyFilter;
	const methods = useForm<BoxFilter>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetBoxTreasureListQuery(methods.getValues(), {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	const { data: allData } = useGetBoxTreasureListQuery(
		{
			searchString: '',
			sortings: [],
			id: boxId,
			selectedList: [0, 1],
			pagination: {
				page: 1,
				size: 999999,
			},
		},
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const { data: selectApiData } = useGetBoxTreasureSelectedListQuery(
		boxId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	useEffect(() => {
		console.log(123123);
		if (allData) {
			const selectedRowsData = allData?.data?.items.filter((row) => {
				if (selectApiData?.data?.find((item) => item == row.gameTreasureId)) {
					return row;
				}
			});

			setSelectedData(selectedRowsData);
		}
	}, [selectApiData]);

	useEffect(() => {}, [methods.watch()]);
	const onSubmit = async () => {
		const response = await updateBoxTreasureList({
			id: boxId || '',
			arr: selectedData.map(
				(item: { gameTreasureId: string }) => item.gameTreasureId
			),
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.fixTreasureSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setOpen(false);
	};
	const handleOpen = () => {
		setOpen(true);
	};

	const columns: GridColDef[] = [
		{
			field: 'gameTypeName',
			headerName: t('box.typeName'),
			flex: 21,
		},
		{
			field: 'gameSeriesName',
			headerName: t('box.seriesName'),
			flex: 21,
		},
		{
			field: 'published',
			headerName: t('box.seriesStatus'),
			flex: 21,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'gameTreasureName',
			headerName: t('box.treasureName'),
			flex: 21,
		},
		{
			field: 'quality',
			headerName: t('chartletList.quality'),
			flex: 21,
			renderCell: (params: any) => showLabel(params.row.quality, qualityList),
		},
	];

	const handlechange = (text: string) => {
		methods.reset({ ...defaultPropertyFilter, searchString: text });
	};

	const handleSelect = (rows: string[]) => {
		const selectedRowsData = allData?.data?.items.filter((row) =>
			rows.includes(row.gameTreasureId || '')
		);
		setSelectedData(selectedRowsData || []);
	};

	const transformResponse = useCallback((response?: BlindBoxGameDTO[]) => {
		if (!response) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				// updatedAt: !item.updatedAt
				// 	? '-'
				// 	: dayjs(item.updatedAt)
				// 			.locale('en')
				// 			.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});

		return newResponse;
	}, []);

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<NoteAltOutlinedIcon />}
				onClick={() => handleOpen()}
			>
				{t('button.fix')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('box.createTreasure')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Box
						display='flex'
						justifyContent='space-between'
						alignItems='center'
						marginBottom={'10px'}
					>
						<Typography
							variant='body2'
							sx={{
								display: 'flex',
							}}
						>
							<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
							&nbsp;
							{t('box.showTreasureTips')}
						</Typography>
					</Box>
					<FormProvider {...methods}>
						<Grid container rowSpacing={2} columnSpacing={1}>
							<Grid item xs={6}>
								<AppSummarySearchBar
									name='searchString'
									control={methods.control}
									placeholder={t('common.search')}
									onchange={(text) => {
										handlechange(text);
									}}
								/>
							</Grid>

							<Grid item xs={6}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='gameTypeList'
									label={t('box.treasureType')}
									options={BOX_SERIES_TYPE.filter(
										(item) => item.id != BoxSeriesType.Chartlet
									)}
									size='small'
									emptyArrayIfNoSelection
								/>
							</Grid>
						</Grid>
						<div style={{ height: '20px' }}></div>
						<AppDataGrid
							checkboxSelection
							loading={!isGetPropertySummarySuccess}
							rows={transformResponse(propertySummary?.data?.items) ?? []}
							rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
							columns={columns}
							getRowId={(row) => row.gameTreasureId}
							onSelectionModelChange={(selectionModel) => {
								handleSelect(selectionModel as string[]);
							}}
							selectionModel={selectedData.map(
								(item: { gameTreasureId: any }) => item.gameTreasureId
							)}
						/>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('box.change')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
